import React from 'react';
import { useTranslation } from 'react-i18next';

import { getCurrencySymbol, printOdd } from 'shared/utils/common';
import { isRacingSport } from 'shared/utils/markets';
import { getTicketSlipResolvedType } from 'shared/utils/ticket-slip-helper';
import { vsOutcomeTypeKey } from 'lib/virtualSportsI18n';
import OutcomeImages from 'shared/components/outcomes/OutcomeImages';
import { useTicketSlipStore } from 'features/tickets/store';
import { UserSessionContext } from 'features/session/userSessionStore';
import { getSportColor, getSportOnColor } from 'shared/utils/sport-colors';

function formatStakeInputValue(stake: number | string | undefined) {
  const numericStake = Number(stake);
  return Number.isFinite(numericStake) && numericStake > 0 ? String(stake) : '';
}

function ModernTipItem(props: {
  event_id?: number | string;
  outcome_id?: number | string;
  n_runners?: number | string;
  outcome?: string | number;
  runner_number?: number | string;
  odd?: number | string;
  sport_id?: number | string;
  description?: string;
  market_name?: string;
  outcome_type_id?: number | string;
  stake?: number | string;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const {
    event_id,
    outcome_id,
    n_runners,
    outcome,
    runner_number,
    odd,
    sport_id,
    description,
    market_name,
    outcome_type_id,
    stake,
    readOnly = false,
  } = props;

  const loading = useTicketSlipStore((s) => (readOnly ? false : s.loading));
  const ticket_slip = useTicketSlipStore((s) => s.ticket_slip);
  const bet_mode = useTicketSlipStore((s) => s.bet_mode);
  const stake_mode = useTicketSlipStore((s) => s.stake_mode);
  const system_rows = useTicketSlipStore((s) => s.system_rows);
  const removeTip = useTicketSlipStore((s) => s.removeTip);
  const updateTip = useTicketSlipStore((s) => s.updateTip);
  const [{ user }] = React.useContext(UserSessionContext);
  const odds_format = user?.odds_format || 'decimal';
  const currencySymbol = getCurrencySymbol(user?.currency);
  const resolvedType = getTicketSlipResolvedType(ticket_slip, bet_mode, system_rows, stake_mode);
  const showStakeInput = !readOnly && ticket_slip.length > 1 && resolvedType !== 'SYSTEM';
  const [stakeInput, setStakeInput] = React.useState(() => formatStakeInputValue(stake));

  React.useEffect(() => {
    setStakeInput(formatStakeInputValue(stake));
  }, [stake]);

  const onDelete = () => {
    if (outcome_id != null) removeTip(outcome_id);
  };

  const commitStakeValue = (rawValue: string) => {
    if (outcome_id == null) {
      return;
    }

    const value = rawValue.trim();
    updateTip({
      outcome_id,
      stake: value === '' ? 0 : Number(value),
    });
  };

  const onStakeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.currentTarget.value.replace(',', '.');
    if (!/^\d*\.?\d*$/.test(rawValue)) {
      return;
    }

    setStakeInput(rawValue);

    if (rawValue === '' || rawValue === '.' || rawValue.endsWith('.')) {
      if (rawValue === '') {
        commitStakeValue('');
      }
      return;
    }

    commitStakeValue(rawValue);
  };

  const onStakeBlur = () => {
    if (stakeInput === '' || stakeInput === '.') {
      commitStakeValue('');
      setStakeInput('');
      return;
    }

    commitStakeValue(stakeInput);
    setStakeInput(formatStakeInputValue(stakeInput));
  };

  if (sport_id == null) return null;

  const sportColor = getSportColor(sport_id);
  const sportOnColor = getSportOnColor(sport_id);

  const tipHeaderContent =
    sport_id == 50 && description ? (
      <>{description}</>
    ) : (
      <>
        {isRacingSport(Number(sport_id)) && n_runners != null && n_runners + ' '}
        {t(`SPORT_${sport_id}`)}
        {sport_id == 40 && description && ' - ' + description}
      </>
    );

  return (
    <div
      className="mx-3 my-2 rounded-lg bg-card border border-border shadow-sm hover:shadow-md transition-shadow overflow-hidden"
      style={{ borderLeftWidth: '3px', borderLeftStyle: 'solid', borderLeftColor: sportColor }}
      data-event={event_id}
      data-tip={outcome_id}
      id={`tip_${outcome_id}`}
    >
      <div
        className="flex items-center justify-between px-3 py-2 border-b border-border"
        style={{ backgroundColor: `${sportColor}15` }}
      >
        <span className="text-xs font-semibold text-foreground truncate">{tipHeaderContent}</span>
        <span className="text-[10px] text-muted-foreground tabular-nums flex-shrink-0 ml-2">
          Ev. #{event_id}
        </span>
      </div>

      <div className="px-3 py-3 flex items-center gap-3">
        <div className="flex items-center gap-1 flex-shrink-0">
          {isRacingSport(sport_id) &&
            outcome &&
            (typeof outcome === 'string' && outcome.includes('-')
              ? outcome.split('-').map((runner, index) => (
                  <OutcomeImages
                    key={index}
                    outcome={runner.trim()}
                    n_runners={n_runners}
                    sport_id={sport_id}
                    br={0}
                  />
                ))
              : (
                  <OutcomeImages
                    outcome={String(runner_number ?? outcome ?? '')}
                    n_runners={n_runners}
                    sport_id={sport_id}
                    br={0}
                  />
                ))}
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-foreground truncate">
            {market_name != null ? market_name : ''}
          </div>
          {(sport_id == 40 || sport_id == 50) && outcome_type_id != null && (
            <div className="text-xs text-muted-foreground mt-0.5">
              <span className="lg:hidden">{t(vsOutcomeTypeKey(Number(outcome_type_id), true))}</span>
              <span className="hidden lg:inline">{t(vsOutcomeTypeKey(Number(outcome_type_id), false))}</span>
            </div>
          )}
          {showStakeInput && (
            <div className="mt-2 flex items-center gap-2">
              <label
                htmlFor={`tip-stake-${outcome_id}`}
                className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                {t('STAKE', 'Stake')}
              </label>
              <span className="text-xs font-semibold text-muted-foreground">{currencySymbol}</span>
              <input
                id={`tip-stake-${outcome_id}`}
                className="w-20 px-2 py-1 text-xs font-semibold border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-neutral text-right bg-background text-foreground"
                disabled={loading}
                readOnly={loading}
                value={stakeInput}
                onChange={onStakeChange}
                onBlur={onStakeBlur}
                placeholder="0.00"
                inputMode="decimal"
                aria-label={t('STAKE', 'Stake')}
              />
            </div>
          )}
        </div>

        <div
          className="flex-shrink-0 text-base font-bold px-3 py-1.5 rounded-md shadow-sm"
          style={{ backgroundColor: sportColor, color: sportOnColor }}
        >
          {printOdd(odd ?? 0, odds_format)}
        </div>

        {!readOnly && (
          <button
            onClick={onDelete}
            className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-40"
            title={t('REMOVE_BET')}
            aria-label={t('REMOVE_BET')}
            disabled={loading}
          >
            <i className="bi bi-x-lg text-xs" />
          </button>
        )}
      </div>
    </div>
  );
}

export default ModernTipItem;
